import { useCallback, useEffect, useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import {
  BCID_APPROVED_REASONS_URL,
  CUSTOM_CALL_REASON_MAX,
  isApprovedCallReason,
} from '../../../domain/constants';
import type { Case } from '../../../domain/initial-cases';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { getCase } from '../../../api/cases';
import {
  listLogoSnapshots,
  LOGO_SNAPSHOT_POLL_DELAYS_MS,
  logoFetchPending,
  logoSnapshotDataUrl,
  refetchLogoSnapshot,
  type LogoSnapshot,
  type LogoSnapshotHistory,
} from '../../../api/logoSnapshots';
import { CallReasonSelect, CTIA_ONBOARDING_LIMITS, mapIntakeToOnboardingData } from './OnboardingTab';
import { RevetChangePanel } from '../../../components/RevetChangePanel';
import { SectionEvidenceAttachments } from '../../../components/EvidenceAttachments';

// #479 — the RCD & SHAFT tab, which sits on the seam between what the CUSTOMER
// asserted (`onboarding.data`) and what 382 ATTESTS (`cases.rcd`).
//
// THE RULE THIS TAB IS BUILT ON, because four separate defects came from not
// having it: what this tab shows for a branded-call claim is exactly what
// `buildDeliveryPlan` (apps/api/src/workers/settlement-delivery.ts) will send to
// CTIA. Same precedence, same limits, same measurement. Every one of the #479
// defects was a place where the screen and the delivery disagreed — a 35-cap on
// a 64-char field, an unbounded field that dead-lettered weeks later, blanks
// where a value existed, and a logo nobody could see. A tab that shows something
// other than what ships is not a review of anything.
//
// The precedences below are copied from buildDeliveryPlan and must not drift:
//   nam          = rcd.displayName ?? onboarding.data.displayName
//   default_crn  = rcd.callReason  ?? onboarding.data.callReason
//   icn_url      =                    onboarding.data.callLogoUrl   (no rcd read)
//
// The logo asymmetry is deliberate — see the comment on the logo panel.

/** Empty-or-missing is "not set", exactly as the worker's `optionalString` has it. */
function claimValue(...candidates: Array<unknown>): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate;
  }
  return '';
}

/**
 * The two editable claims, each with the limit CTIA actually applies to it.
 *
 * Imported, never re-typed. The whole of #479 item 1 was two numbers written out
 * by hand on this tab: 35 applied to `default_crn` (whose limit is 64, so a
 * valid 40-character reason was silently truncated) and nothing at all applied
 * to `nam` (whose limit is 35, so an over-length name reached the settlement
 * worker and dead-lettered weeks later). The drift WAS the bug, so the constant
 * is the import and the CTIA field name travels with it.
 */
const CLAIM_FIELDS = [
  {
    key: 'displayName' as const,
    label: 'Caller Display Name',
    claim: 'nam',
    limit: CTIA_ONBOARDING_LIMITS.displayName,
    hint: 'Must be legal name, brand name, or DBA. No emojis, non-Latin chars, or hyperlinks.',
  },
  {
    key: 'callReason' as const,
    label: 'Call Reason',
    claim: 'default_crn',
    limit: CTIA_ONBOARDING_LIMITS.callReason,
    hint: null,
  },
];

type ClaimKey = (typeof CLAIM_FIELDS)[number]['key'];

/**
 * The cap that actually applies to a claim's CURRENT value.
 *
 * #541 split the call reason in two: a value on the BCID pre-approved list may
 * run to the CTIA Platform's 64-character `default_crn` width, while a CUSTOM
 * reason is capped at 35 by the BCBP. Every other claim keeps its single limit.
 *
 * Measured against the value in hand, because which cap applies is a property of
 * the VALUE and not of the field — the same box holds both kinds. Exact match,
 * for the reason `isApprovedCallReason` documents: what CTIA receives is the
 * stored bytes.
 */
function limitFor(field: (typeof CLAIM_FIELDS)[number], value: string): number {
  if (field.key !== 'callReason') return field.limit;
  if (value === '' || isApprovedCallReason(value)) return field.limit;
  return CUSTOM_CALL_REASON_MAX;
}


/**
 * Whether the browser will be allowed to load this image at all.
 *
 * MEASURED FROM THE DEPLOYED HEADER, not assumed: the portal and SPA are served
 * under `img-src 'self' data:`
 * (docs/deployment/files/nginx/snippets/bcid-security-headers.conf), which
 * permits same-origin images and data: URIs and nothing else. A customer's logo
 * is on the customer's own host, so the browser refuses the request before it is
 * made.
 *
 * The check is done UP FRONT rather than by rendering an <img> and waiting for
 * onError, and that is not a style preference. A blocked load also POSTs a
 * violation to /api/v1/csp-report — a real monitoring surface — so rendering an
 * image we already know is blocked would file a security report on every case
 * an analyst opens, and the fastest way to make a CSP report endpoint useless is
 * to fill it with violations that are expected.
 *
 * Widening img-src is not the fix and was not done. What an inline <img> would
 * newly leak is the ANALYST'S BROWSER — their IP, their user agent, and the
 * timing of each individual case open — to a company that is currently under
 * review by them. (382's own worker already fetches these URLs server-side
 * under the #284 SSRF policy, so the URL being reachable is not the new
 * exposure; who is reaching it is.)
 *
 * Deliberately NOT a format check. A same-origin PNG is still an image an
 * analyst can SHAFT-review, and refusing to show it would hide the very thing
 * this panel exists for; CTIA's ".bmp, 256×256, 24-bit" rule is enforced where
 * the value is entered and accepted (ctiaOnboardingErrors, and the intake API),
 * not here. This function answers one question: will the browser load it.
 */
export function logoDisplayability(url: string): 'ok' | 'blocked-by-csp' | 'unusable' {
  const trimmed = url.trim();
  if (trimmed === '') return 'unusable';
  if (/^data:image\//i.test(trimmed)) return 'ok';
  // A root-relative path is same-origin by definition and is the only relative
  // form accepted. Resolving arbitrary text against the page URL would classify
  // "not a url at all" as a same-origin path and render a broken image for it —
  // `new URL(text, base)` succeeds on almost any string, so parsing alone is not
  // a validity check.
  if (trimmed.startsWith('/')) return 'ok';
  try {
    const resolved = new URL(trimmed);
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return 'unusable';
    return resolved.origin === window.location.origin ? 'ok' : 'blocked-by-csp';
  } catch {
    return 'unusable';
  }
}

// #767/#810 — the backoff the panel polls a queued snapshot on, shared with
// the Onboarding tab's wait after a URL save (api/logoSnapshots.ts).
export const SNAPSHOT_POLL_DELAYS_MS = LOGO_SNAPSHOT_POLL_DELAYS_MS;

/**
 * #768 — true when the rollout baseline's fetch of this case's logo failed and
 * no snapshot has been reviewed since: the case the runbook says is "listed for
 * an analyst". Exported for the tab's tests.
 */
export function rolloutFailedUnreviewed(history: Pick<LogoSnapshotHistory, 'items' | 'reviewedSnapshotId'> | null): boolean {
  if (!history || history.reviewedSnapshotId !== null) return false;
  return history.items.some((s) => s.trigger === 'ROLLOUT' && s.outcome === 'failed');
}

const TRIGGER_LABEL: Record<LogoSnapshot['trigger'], string> = {
  PROMOTION: 'on promotion',
  CASE_CREATE: 'on case creation',
  URL_CHANGE: 'after the URL changed',
  REFETCH: 'on re-fetch',
  ROLLOUT: 'when logo re-vetting was deployed',
  DRIFT_CHECK: 'by the daily logo check',
};

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/**
 * #767 — the logo as the SERVER fetched it, with its history and a re-fetch.
 *
 * Every image here is the server's stored copy, read with the bearer token and
 * rendered as a `data:` URL (api/logoSnapshots.ts) — never the registrant's
 * URL, and never a `blob:` URL, which the CSP refuses. So opening a case tells
 * the registrant nothing. The SHAFT review reset on a changed logo is the
 * SERVER's (lib/logo-snapshot.ts in the API); when a snapshot reports one, the
 * case is re-read so the checkbox below shows it.
 */
function useLogoSnapshots(caseId: string, logoUrl: string, onCaseChanged: () => void) {
  const [history, setHistory] = useState<LogoSnapshotHistory | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [images, setImages] = useState<Record<number, { url?: string; error?: string }>>({});
  const [polls, setPolls] = useState(0);

  const load = useCallback(async () => {
    try {
      const next = await listLogoSnapshots(caseId);
      setHistory(next);
      setListError(null);
      return next;
    } catch (err) {
      setListError(err instanceof Error ? err.message : String(err));
      return null;
    }
  }, [caseId]);

  const showImage = useCallback((snapshotId: number) => {
    setImages((prev) => (prev[snapshotId] ? prev : { ...prev, [snapshotId]: {} }));
    logoSnapshotDataUrl(caseId, snapshotId)
      .then((url) => setImages((prev) => ({ ...prev, [snapshotId]: { url } })))
      .catch((err) => setImages((prev) => ({
        ...prev, [snapshotId]: { error: err instanceof Error ? err.message : String(err) },
      })));
  }, [caseId]);

  // A new case, or a new URL on this one: start over.
  useEffect(() => {
    setHistory(null);
    setImages({});
    setPolls(0);
    if (logoUrl !== '') void load();
  }, [caseId, logoUrl, load]);

  const current = history?.items[0] ?? null;

  // Show the current snapshot's image as soon as it is `ok`.
  useEffect(() => {
    if (current?.outcome === 'ok' && !images[current.id]) showImage(current.id);
  }, [current, images, showImage]);

  // A queued snapshot is settled by the worker; ask again until it is. When it
  // settles, the server may have bumped the case version (a new image always
  // does, and a reset with it — #810; since #768 a FAILED fetch of a new URL
  // resets the review too), so the case is re-read then: the tab's next edit
  // carries the current If-Match instead of 409ing.
  useEffect(() => {
    if (current?.outcome !== 'pending' || polls >= SNAPSHOT_POLL_DELAYS_MS.length) return undefined;
    const timer = setTimeout(() => {
      setPolls((n) => n + 1);
      void load().then((next) => {
        const settled = next?.items.find((item) => item.id === current.id);
        if (settled && settled.outcome !== 'pending') onCaseChanged();
      });
    }, SNAPSHOT_POLL_DELAYS_MS[polls]);
    return () => clearTimeout(timer);
  }, [current, polls, load, onCaseChanged]);

  return {
    history, listError, images, showImage, load, current,
    pollsExhausted: polls >= SNAPSHOT_POLL_DELAYS_MS.length,
  };
}

// #766 — the SHAFT review tick (logo and content), exported so the case progress
// tracker lists it as open on exactly the condition this tab's checkbox shows
// unticked.
//
// #810 — strict JSON `true`, the value the checkbox writes, and nothing else:
// the SAME rule as the API's `isShaftReviewed` (apps/api/src/lib/logo-snapshot.ts),
// which stamps and resets the review. If the two disagreed on a stored 1 or
// "true", the screen would show a review the server does not treat as ticked.
// Pinned both ways (RcdShaftTab.test.tsx, logo-snapshot.test.ts).
export function shaftReviewed(c: Pick<Case, 'shaft'>): boolean {
  return (c.shaft as { reviewed?: unknown } | null | undefined)?.reviewed === true;
}

export function RcdShaftTab({ c }: { c: Case }) {
  const { updateCase, mergeCases } = useAppData();
  // Schema default for `rcd` and `shaft` is `'{}'::jsonb`, so freshly-created
  // cases come back with empty objects (no displayName / callReason / reviewed
  // keys). Direct property reads are fine; the killer was `c.rcd.callReason.length`
  // — `.length` on undefined throws.
  const rcd = (c.rcd ?? {}) as { displayName?: string; callReason?: string; callLogoUrl?: string };
  const shaft = (c.shaft ?? {}) as { reviewed?: boolean };
  const ob = (c.onboarding ?? {}) as { status?: string; data?: Record<string, any>; intake?: Record<string, any> };
  // The same read-side fallback the Onboarding tab uses (#146): cases promoted
  // before the handler populated onboarding.data carry only the raw intake
  // payload. Without this, the provenance below would show blanks for exactly
  // the oldest cases — which is the defect this tab is fixing, one layer down.
  const obData: Record<string, any> = (ob.data && Object.keys(ob.data).length > 0)
    ? ob.data
    : (ob.intake ? mapIntakeToOnboardingData(ob.intake) : {});

  // Before the onboarding is submitted there is no attested claim to edit — the
  // customer's application is the source of truth and the Onboarding tab is
  // where it is corrected. Editing here first was defect #479-2: Submit
  // Onboarding folds `data.displayName || rcd.displayName` forward, so the
  // customer's original silently replaced the analyst's correction, with no
  // prompt and no audit entry telling the two apart.
  //
  // A case with no onboarding record at all (staff-created, never an intake) has
  // no other source of truth, so it stays editable — locking that would leave
  // the claim uneditable anywhere.
  const preSubmit = ob.status === 'DRAFT';

  // A draft holds ONLY text that was refused, and only while it is refused.
  // Anything savable goes straight to the case and is dropped from here, so the
  // input returns to being driven by `rcd`.
  //
  // Keeping a draft after a successful save looks harmless and is not. The case
  // write can fail and roll back — a 409 from the If-Match chain, a dropped
  // request — and an input still showing the local copy would then display,
  // count, and compare against a value the case does not hold. The field would
  // read as saved when it had been rejected, which on this tab means an analyst
  // believing they had corrected a claim that is still wrong. Before this tab
  // held drafts at all, the input was driven by the case alone and a rollback
  // was visible; that behaviour is what the delete below preserves.
  const [drafts, setDrafts] = useState<Partial<Record<ClaimKey, string>>>({});
  // #612 — AND IT IS EMPTIED ON A CASE CHANGE, exactly as the Onboarding tab
  // empties its own. This tab is reused across cases rather than remounted, so
  // without this the map outlives the case whose keystrokes filled it: a refused
  // over-cap reason or over-35 display name typed on one case rendered over the
  // NEXT one an analyst opened, red border, “Not saved” and all, on a case that
  // never had that edit. Nothing was written wrongly — `commit` refuses the value
  // on every case — but the screen said something false about the case on it,
  // which is the same failure the `key={c.id}` remounts below already guard.
  //
  // ORDERING, and it is the reason the seed below reads a stored value. An
  // effect runs AFTER the commit whose render `key={c.id}` remounted the
  // per-case controls in, so for that one render this map still holds the
  // OUTGOING case's drafts. Anything computed at mount from a draft-first value
  // therefore reads the previous case's refused keystrokes and, if it latches,
  // keeps them. Mount-time seeds must read stored values (`rcd`), never `drafts`.
  useEffect(() => setDrafts({}), [c.id]);

  const commit = (field: ClaimKey, value: string) => {
    // `readOnly` on the input stops a person typing; it does not stop a
    // programmatic change, and a guard that lives only in an attribute is one
    // refactor away from being gone. The rule is enforced here, where the write
    // happens.
    if (preSubmit) return;
    // NOT `maxLength`, and this is the heart of item 1. A maxLength input drops
    // the tail of a pasted value without a word — silent truncation of a
    // consumer-facing RCD claim, which is the precise failure the settlement
    // module refuses to commit and the reason `default_crn` is validated rather
    // than trimmed. So the over-length text stays visible and is simply not
    // SAVED, with the field saying so. Nothing is lost silently and nothing that
    // would dead-letter at CTIA can reach `rcd`.
    //
    // Measured RAW, like the worker (`nam.length > MAX_NAM` on the un-trimmed
    // string): 35 characters plus a trailing space is 36 to CTIA, and CTIA's is
    // the measurement that decides whether the DIR is accepted. The Onboarding
    // submit gate measured the TRIMMED length until #485 and let exactly that
    // value through to dead-letter at settlement; all three places — here,
    // ctiaOnboardingErrors, and buildDeliveryPlan — now count the same
    // characters, and a change to one belongs in the same commit as the others.
    const limit = limitFor(CLAIM_FIELDS.find((f) => f.key === field)!, value);
    if (value.length > limit) {
      setDrafts((d) => ({ ...d, [field]: value }));
      return;
    }
    setDrafts((d) => {
      if (!(field in d)) return d;
      const next = { ...d };
      delete next[field];
      return next;
    });
    updateCase(c.id, { rcd: { ...rcd, [field]: value } });
  };

  const logoUrl = claimValue(obData.callLogoUrl);
  // The server bumped the case version — it reset the SHAFT review because a
  // newer snapshot differs from the logo it was ticked against, or a new image
  // settled (#810) — so re-read the case: the checkbox below must not keep
  // showing a tick the server has removed, and the next edit needs the new
  // If-Match.
  const refreshCase = useCallback(() => {
    getCase(c.id).then((fresh) => mergeCases([fresh])).catch(() => { /* the #148 poll catches up */ });
  }, [c.id, mergeCases]);
  const snapshots = useLogoSnapshots(c.id, logoUrl, refreshCase);
  const [refetching, setRefetching] = useState(false);
  const [refetchError, setRefetchError] = useState<string | null>(null);
  const refetch = () => {
    setRefetching(true);
    setRefetchError(null);
    refetchLogoSnapshot(c.id)
      .then(async ({ snapshot, shaftReviewReset }) => {
        await snapshots.load();
        // An image may have bumped the case version (#810), and a reset
        // certainly did — including #768's reset on a failed fetch of a new
        // URL. A failed re-fetch of the same URL changes nothing on the case.
        if (snapshot.outcome === 'ok' || shaftReviewReset) refreshCase();
      })
      .catch((err) => setRefetchError(err instanceof Error ? err.message : String(err)))
      .finally(() => setRefetching(false));
  };
  const current = snapshots.current;
  // #810 — the API refuses a tick while the newest snapshot is pending; the
  // box says so instead of letting the analyst find out from a failed save.
  const tickHeld = !shaftReviewed(c) && logoFetchPending(snapshots.history);
  const currentImage = current?.outcome === 'ok' ? snapshots.images[current.id]?.url : undefined;
  const older = snapshots.history?.items.slice(1) ?? [];
  // The link-out is SECONDARY now: it is the one way left to reach the
  // registrant's server from the analyst's browser, so it says what it costs.
  const linkable = logoUrl !== '' && logoDisplayability(logoUrl) !== 'unusable';
  const logoReviewNote = logoUrl === ''
    ? '🔗 No logo was supplied — nothing to inspect for this one'
    : currentImage
      ? '🔗 Compare against the logo shown above'
      : '🔗 No snapshot is on screen above — re-fetch the logo before ticking';

  return (
    <div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Rich Call Data (RCD) Content Review</h3>
        {preSubmit && (
          <div style={{
            fontSize: 12, color: C.textMuted, marginBottom: 16, padding: '8px 12px',
            background: C.bgInput, borderRadius: 6, borderLeft: `3px solid ${C.warn}`,
          }}>
            Nothing is attested yet. These become 382&rsquo;s claims when you click{' '}
            <strong>Submit Onboarding</strong>, which copies the values from the{' '}
            <strong>Onboarding</strong> tab — so corrections belong there, not here. Editing them on
            this tab before then would be overwritten by that copy.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {CLAIM_FIELDS.map((field) => {
            const attested = rcd[field.key] ?? '';
            const shown = drafts[field.key] ?? attested;
            const requested = claimValue(obData[field.key]);
            const limit = limitFor(field, shown);
            const pending = drafts[field.key] !== undefined;
            // #599 — A REFUSAL IS ABOUT A PENDING EDIT, FOR EVERY CLAIM.
            //
            // `drafts` holds a value only while `commit` has refused to save it,
            // so `pending` is exactly the "you typed this and it was rejected"
            // case — and that is the only case in which "Not saved" is a true
            // sentence. #541 established the rule for the call reason, which had
            // gained a second, smaller cap: a CUSTOM value is capped at 35 by the
            // BCBP, so a reason stored before the pre-approved list existed can
            // sit above its cap through no action of the analyst's, and colouring
            // that as a refused edit accuses them of something they did not do.
            //
            // #599 is the same contradiction under `nam`, and it was the louder
            // one: a stored 36-character display name was labelled "Not saved" by
            // a message whose own next clause admitted "the case still holds"
            // that very value. Those rows are reachable rather than hypothetical
            // — the Onboarding submit gate measured TRIMMED length until #485, so
            // a 35-character name plus a trailing space folded forward as a
            // 36-character `nam`, and the #479-1 note records one reaching the
            // settlement worker. `commit` has refused over-length values since
            // #479, so no NEW row can be created; the existing ones still have to
            // be told the truth until an analyst shortens them.
            const over = pending && shown.length > limit;
            // Both stored-over-limit disclosures are gated on the FIELD, not
            // merely on the arithmetic, because what is TRUE of an over-limit
            // stored value differs per claim and the copy has to differ with it.
            // `limitFor` returns 35 for `displayName` too (that is simply `nam`'s
            // limit), so an ungated disclosure would tell a stored 36-character
            // display name that it "predates the BCID pre-approved list and is
            // above the cap for a custom reason" — call-reason advice under the
            // display-name box (the PR #596 review finding).
            const legacyOverCap = field.key === 'callReason'
              && !pending && shown !== '' && shown.length > limit;
            // The `nam` half of #599. Deliberately NOT the call reason's wording,
            // and the difference is a fact about delivery rather than a matter of
            // tone: an over-length custom reason still FITS `default_crn`, whose
            // CTIA limit is 64, so it genuinely delivers as shown. An over-length
            // `nam` does not — `buildDeliveryPlan` pushes "display name exceeds
            // CTIA nam limit" onto `errors` and returns `ok: false`, so the DIR is
            // never sent at all. Telling this one it "delivers as shown" would
            // trade one false statement for another, and the rule this tab is
            // built on is that what it shows is what the worker will send.
            const storedOverNam = field.key === 'displayName'
              && !pending && shown.length > limit;
            // What the settlement worker would send today, by its own
            // precedence — which pre-submit is the customer's requested value.
            const effective = claimValue(attested, requested);
            return (
              <div key={field.key}>
                <label htmlFor={`rcd-${field.key}`} style={{ fontSize: 11, color: C.textMuted }}>
                  {field.label} <span style={{ opacity: 0.7 }}>({field.claim}, ≤{limit} chars)</span>
                </label>
                {field.key === 'callReason' ? (
                  // #541 — a choice, not free text. Both callbacks go straight
                  // to `commit`, so the over-length guard below is exactly the
                  // one that has always governed this box.
                  <CallReasonSelect
                    // Remount per case, for the reason the Onboarding tab's
                    // call site spells out: the control remembers an opened
                    // custom box and this tab outlives the case it was showing.
                    key={c.id}
                    id={`rcd-${field.key}`}
                    value={shown}
                    // The ATTESTED value, for the mount-time seed only. Not
                    // `shown`, which is draft-first: `drafts` here holds text
                    // `commit` REFUSED, and the #612 effect that empties it on a
                    // case change runs one render AFTER this remount — so on
                    // that render `shown` is still the OUTGOING case's refused
                    // keystrokes, and seeding off it would let them decide
                    // whether the next case's box opens, and latch it there.
                    storedValue={attested}
                    readOnly={preSubmit}
                    onSelect={(next) => commit('callReason', next)}
                    onCustomInput={(next) => commit('callReason', next)}
                  />
                ) : (
                  <input
                    id={`rcd-${field.key}`}
                    style={{
                      ...styles.input,
                      ...(over ? { borderColor: C.danger } : {}),
                    }}
                    value={shown}
                    readOnly={preSubmit}
                    placeholder={preSubmit ? 'Not attested yet' : ''}
                    onChange={(e) => commit(field.key, e.target.value)}
                  />
                )}
                <div style={{ fontSize: 11, color: over ? C.danger : C.textMuted, marginTop: 4 }}>
                  {shown.length}/{limit} chars
                  {field.key === 'callReason' && (
                    <>
                      {' · '}
                      <a
                        href={BCID_APPROVED_REASONS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: C.goldText }}
                      >BCID Approved List ↗</a>
                    </>
                  )}
                </div>
                {/* The disclosure that makes refusing to save honest rather than
                    mysterious: the box holds text the case does not. */}
                {over && (
                  <div style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>
                    ⚠ Not saved — {limit === CUSTOM_CALL_REASON_MAX && field.key === 'callReason'
                      ? `the BCBP limits a custom call reason to ${limit} characters`
                      : `CTIA limits ${field.claim} to ${limit} characters`} and this is{' '}
                    {shown.length}. Shorten it to save; the case still holds{' '}
                    {attested === '' ? 'no value' : `“${attested}”`}.
                  </div>
                )}
                {legacyOverCap && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    This custom reason predates the BCID pre-approved list and is {shown.length}{' '}
                    characters, above the {limit}-character cap for a custom reason. It is stored
                    and delivers as shown; choose a pre-approved reason or shorten it to bring it
                    into line.
                  </div>
                )}
                {storedOverNam && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    This name is stored and attested at {shown.length} characters, above
                    CTIA&rsquo;s {limit}-character {field.claim} limit. Settlement refuses a DIR
                    whose {field.claim} is over the limit, so shorten it here to make this case
                    deliverable.
                  </div>
                )}
                {/* #479-3 — provenance. `cases.rcd` is '{}'::jsonb until the
                    onboarding is submitted, so this tab used to open on empty
                    boxes while the customer's requested values sat one tab over.
                    "No data" and "not attested yet, here is what was asked for"
                    are different statements about a case. */}
                {requested !== '' && requested !== attested && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    Requested by the customer: <strong style={{ color: C.text }}>{requested}</strong>
                    {attested === ''
                      ? ' — not yet attested.'
                      : ' — differs from the attested value above, which is what will be delivered.'}
                  </div>
                )}
                {effective === '' && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    Neither attested nor requested — nothing to deliver for {field.claim}.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* #479-4 — the logo, on the tab whose checklist asks somebody to look at
          it. #767 — as the SERVER fetched it: never the registrant's URL. */}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Call Logo (icn)</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
          The image the Logo Visual Analysis checklist below is about. CTIA requires a 256×256
          24-bit .bmp. The URL is the <strong>Onboarding</strong> tab&rsquo;s value. The portal
          fetches it on its server and shows that copy, so your browser never contacts the
          registrant&rsquo;s server. What CTIA is sent is the snapshot the SHAFT review was ticked
          against — its URL and the hash of its stored image, never a fresh download — and on an
          approved registration a changed logo reaches CTIA only through the logo re-vet below.
        </div>
        {/* #768 — the logo re-vet, beside the logo it is about. Renders nothing
            unless one is open on this case. Keyed on the newest snapshot and
            the case version, so a fetch that opens or moves a re-vet, or an act
            on it, re-reads it rather than leaving the mount-time answer up. */}
        <RevetChangePanel
          key={`${snapshots.current?.id ?? 'none'}:${c.version}`}
          caseId={c.id}
          kind="logo"
        />
        {logoUrl === '' && (
          <div style={{ fontSize: 12, color: C.textMuted }}>
            No call logo URL on this application — there is no image to review, and{' '}
            <code>icn_url</code> will be absent from the DIR.
          </div>
        )}
        {logoUrl !== '' && (
          <>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8, wordBreak: 'break-all' }}>
              <code>{logoUrl}</code>
            </div>
            {snapshots.listError && (
              <div style={{ fontSize: 12, color: C.danger }}>
                Could not load the logo snapshots: {snapshots.listError}
              </div>
            )}
            {!snapshots.listError && !snapshots.history && (
              <div style={{ fontSize: 12, color: C.textMuted }}>Loading the logo snapshot…</div>
            )}
            {snapshots.history && !current && (
              <div style={{ fontSize: 12, color: C.textMuted }}>
                No snapshot of this logo has been taken yet. Re-fetch it to see it here.
              </div>
            )}
            {current?.outcome === 'pending' && (
              <div style={{ fontSize: 12, color: C.textMuted }}>
                {snapshots.pollsExhausted
                  ? `Still queued on the server since ${when(current.requestedAt)} — re-fetch to try now.`
                  : `Fetching the logo on the server — queued ${when(current.requestedAt)}.`}
              </div>
            )}
            {/* #768 — the one-time rollout baseline could not fetch this
                logo, and no review stands on one since: the case is listed
                for an analyst here, where the fix is. */}
            {rolloutFailedUnreviewed(snapshots.history) && (
              <div
                role="alert"
                style={{
                  fontSize: 12, padding: '10px 12px', borderRadius: 6, marginBottom: 8,
                  border: `1px solid ${C.warn}`, background: C.warnLight, color: C.text,
                }}
              >
                <strong>The rollout baseline could not fetch this logo.</strong> When logo re-vetting
                was deployed, the portal tried to record the logo this registration was reviewed and
                delivered with, and the fetch failed, so no reviewed logo stands on it. Re-fetch the logo
                and have it reviewed — on an approved registration that is a logo re-vet. Until then a
                change that needs a new display record is refused at delivery for want of a reviewed
                logo.
              </div>
            )}
            {current?.outcome === 'failed' && (
              <div style={{
                fontSize: 12, padding: '10px 12px', borderRadius: 6,
                background: C.bgInput, borderLeft: `3px solid ${C.warn}`, color: C.textMuted,
              }}>
                <div style={{ fontWeight: 600, color: C.warn, marginBottom: 4 }}>
                  Fetch failed: {current.failureReason}, at {when(current.fetchedAt)}.
                </div>
                {current.trigger === 'DRIFT_CHECK'
                  ? 'The daily logo check could not fetch the delivered logo. Nothing was sent to CTIA, and the '
                    + 'reviewed logo is still the one delivered; handsets show it only while the URL serves those '
                    + 'bytes. '
                  : 'There is no image to review until a fetch succeeds. '}
                Check the URL on the{' '}
                <strong>Onboarding</strong> tab, then re-fetch.
              </div>
            )}
            {current?.outcome === 'ok' && (
              <div>
                {currentImage ? (
                  <img
                    src={currentImage}
                    alt="Call logo submitted for this registration"
                    style={{
                      width: 128, height: 128, objectFit: 'contain',
                      background: C.bgInput, borderRadius: 8, border: `1px solid ${C.border}`,
                    }}
                  />
                ) : snapshots.images[current.id]?.error ? (
                  <div style={{ fontSize: 12, color: C.danger }}>
                    The snapshot could not be shown: {snapshots.images[current.id]?.error}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: C.textMuted }}>Loading the image…</div>
                )}
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, wordBreak: 'break-all' }}>
                  Fetched {when(current.fetchedAt)} {TRIGGER_LABEL[current.trigger]}
                  {current.fetchedByName ? ` by ${current.fetchedByName}` : ''} · {current.contentType},{' '}
                  {current.byteLength} bytes · SHA-256 <code>{current.sha256}</code>
                </div>
                {snapshots.history?.reviewedSnapshotId === current.id && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                    The SHAFT review below was ticked against this snapshot.
                  </div>
                )}
                {current.resetShaftReview && (
                  <div style={{ fontSize: 12, color: C.warn, marginTop: 4 }}>
                    This logo differs from the one the SHAFT review was ticked against, so the
                    review was reset. Review it again below.
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
                disabled={refetching}
                onClick={refetch}
              >
                {refetching ? 'Re-fetching…' : 'Re-fetch logo'}
              </button>
              {refetchError && (
                <span style={{ fontSize: 12, color: C.danger, marginLeft: 8 }}>
                  Re-fetch failed: {refetchError}
                </span>
              )}
            </div>
            {older.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Earlier snapshots</div>
                <ul style={{ fontSize: 11, color: C.textMuted, margin: 0, paddingLeft: 16 }}>
                  {older.map((snap) => (
                    <li key={snap.id} style={{ marginBottom: 6 }}>
                      {snap.outcome === 'ok'
                        ? `Fetched ${when(snap.fetchedAt)} ${TRIGGER_LABEL[snap.trigger]} · SHA-256 ${snap.sha256?.slice(0, 12)}…`
                        : snap.outcome === 'failed'
                          ? `Fetch failed: ${snap.failureReason}, at ${when(snap.fetchedAt)}`
                          : `Queued ${when(snap.requestedAt)}, never fetched`}
                      {snap.sourceUrl !== logoUrl && <> · from <code>{snap.sourceUrl}</code></>}
                      {snapshots.history?.reviewedSnapshotId === snap.id && ' · the SHAFT review was ticked against this one'}
                      {snap.outcome === 'ok' && !snapshots.images[snap.id] && (
                        <>
                          {' '}
                          <button
                            type="button"
                            style={{ ...styles.btnUnstyled, color: C.goldText }}
                            onClick={() => snapshots.showImage(snap.id)}
                          >View</button>
                        </>
                      )}
                      {snapshots.images[snap.id]?.url && (
                        <div>
                          <img
                            src={snapshots.images[snap.id]?.url}
                            alt={`Call logo snapshot ${snap.id}`}
                            style={{ width: 64, height: 64, objectFit: 'contain', marginTop: 4 }}
                          />
                        </div>
                      )}
                      {snapshots.images[snap.id]?.error && (
                        <span style={{ color: C.danger }}> — could not be shown: {snapshots.images[snap.id]?.error}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 12 }}>
              {linkable ? (
                <>
                  <a
                    href={logoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: C.goldText }}
                  >Open the original in a new tab ↗</a>{' '}
                  — a last resort: your browser would contact the registrant&rsquo;s server, which
                  tells them the case was opened, and when. Prefer the snapshot above.
                </>
              ) : (
                <>
                  It is not an http(s) address, so there is nothing a browser or the server can
                  fetch. Check it on the <strong>Onboarding</strong> tab, which is also where the
                  CTIA format rules (a 256×256 24-bit <code>.bmp</code>) are enforced.
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.warn }}>SHAFT Content Review</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>S.H.A.F.T. = Sex/Adult · Hate Speech/Profanity · Alcohol · Firearms · Tobacco</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            // The instruction for actually SEEING the logo belongs in the column
            // that asks for it to be inspected, not only in the panel above:
            // this list is what an analyst reads while deciding whether to tick
            // the box, and on most cases the image is not on screen to compare
            // against.
            { title: 'Logo Visual Analysis', items: [logoReviewNote, '🍸 Alcohol: martini glasses, hops, grapes', '🌿 Tobacco: green leaves, clouds, vapor, "710", "420"', '🎯 Firearms: crosshairs, bullets, shields', '🔍 Hidden: embedded URLs, QR codes', '🎨 Colors: "Electric Green" / "Leaf Green" = cannabis flag', '🚫 Prohibited: govt agency mimicry (FBI, Police)'] },
            { title: 'Call Reason Keywords', items: ['Sex: "chat", "meet", "hot" + CTA', 'Hate: slurs, exclusionary, inflammatory', 'Alcohol: "happy hour", "drinks on us", brands', 'Firearms: weapon model mentions', 'Tobacco: "pre-roll", "vape", "special flower"', '⚠ Vague urgency: "Flash Sale: 50% off all green"'] },
            { title: 'Display Name Detection', items: ['Character swaps: 41coh0l, Sm0ke, V4pe, H0t Singles, B33r', 'Industry shorthand: CBD, THC, D8', 'Firearms codes: 2A, 2nd amend.', 'Adult: XXX'] },
          ].map((panel, i) => (
            <div key={i} style={{ background: C.bgInput, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{panel.title}</div>
              <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.6 }}>
                {panel.items.map((item, j) => <div key={j}>{item}</div>)}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, ...styles.checkItem }}>
          <input type="checkbox" checked={shaftReviewed(c)} disabled={tickHeld} onChange={e => {
            // The same hold as the API's 422 (#810): never TICK while the
            // logo is still being fetched. Unticking is always allowed.
            if (e.target.checked && tickHeld) return;
            updateCase(c.id, {
              shaft: { ...shaft, reviewed: e.target.checked },
              action: { code: 'SHAFT_REVIEW', detail: { reviewed: e.target.checked } },
            });
          }} />
          <span style={{ fontWeight: 600 }}>Manual SHAFT review completed — no prohibited content detected</span>
        </div>
        {tickHeld && (
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>
            The logo is still being fetched, so there is nothing to review yet. Tick this once
            the logo is shown above (or the fetch is reported as failed).
          </div>
        )}
        {/* #774 — screenshots of what the SHAFT review looked at. */}
        <SectionEvidenceAttachments caseId={c.id} section="shaft" />
      </div>
    </div>
  );
}
